import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import { SelectedDates } from "@/utils/Sale/initialFilters";
import { dateFormats, toJalali, toUtc } from "@payping/utils/dateUtils";
import {
  Col,
  Flex,
  LegacyCard,
  Row,
  Segmented,
  Skeleton,
  Space,
  Tooltip as TooltipWeb,
  Typography,
  useMessagedAsyncRetry,
  usePaypingContext,
} from "@payping/web-components";
import { useAppDispatch } from "@/utils/redux";
import { getSummary } from "@payping/redux/actions";
import { useDateRanges } from "@/utils/useRangeDate";
import { CuToman, EmptyChart, InfoCircleOutlined } from "@payping/icons";
import { useAppTranslation } from "@/utils/useAppTranslation";
import { formatLocalizedNumber } from "@payping/utilities-shared/number";
import { chartMonthId, chartWeekId, chartYearId } from "@/assets/googleTrackIds";
import { enumNumbers, searchParamsParser } from "@/core/utils/search-params-parser";
import { useQueryStates } from "nuqs";

function calculateGrowthPercentage(oldValue: number, newValue: number) {
  if (oldValue === 0) {
    return newValue >= 0 ? 100 : -100;
  }

  const percentageChange = ((newValue - oldValue) / Math.abs(oldValue)) * 100;
  return Math.round(percentageChange);
}

const ReportMobile = () => {
  const { t } = useAppTranslation();
  const dateRanges = useDateRanges();
  const { breakpoints } = usePaypingContext();
  const { md } = breakpoints;
  const dispatch = useAppDispatch();

  // search params parse
  const [searchParams, setSearchParams] = useQueryStates({
    clientId: searchParamsParser.parseAsStringArray,
    selectedDateType: searchParamsParser
      .parseAsIntegerEnum<SelectedDates>(enumNumbers(SelectedDates))
      .withDefault(SelectedDates.yearly),
    terminalId: searchParamsParser.parseAsInteger,

    currentFromDate: searchParamsParser.parseAsString,
    currentToDate: searchParamsParser.parseAsString,
    previousFromDate: searchParamsParser.parseAsString,
    previousToDate: searchParamsParser.parseAsString,
  });

  const selectedDateType: SelectedDates = searchParams.selectedDateType ?? SelectedDates.yearly;

  const { value: dataChart, loading } = useMessagedAsyncRetry(
    async () => {
      // get data for this date
      const thisDate = await dispatch(
        getSummary({
          clientId: searchParams.clientId ?? undefined,
          fromDate: toUtc({
            date: searchParams.currentFromDate || dateRanges.initialThisDate.startDate,
            dateFormat: dateFormats.ui.jalaliDate,
            toFormat: dateFormats.standard.utcDate,
          }).string,
          terminalId: searchParams.terminalId ?? undefined,
          toDate: toUtc({
            date: searchParams.currentToDate || dateRanges.initialThisDate.endDate,
            dateFormat: dateFormats.ui.jalaliDate,
            toFormat: dateFormats.standard.utcDate,
          }).string,
        }),
      );

      // get data for last date
      const lastDate = await dispatch(
        getSummary({
          clientId: searchParams.clientId ?? undefined,
          fromDate: toUtc({
            date: searchParams.previousFromDate || dateRanges.initialLastDate.startDate,
            dateFormat: dateFormats.ui.jalaliDate,
            toFormat: dateFormats.standard.utcDate,
          }).string,
          terminalId: searchParams.terminalId ?? undefined,
          toDate: toUtc({
            date: searchParams.previousToDate || dateRanges.initialLastDate.endDate,
            dateFormat: dateFormats.ui.jalaliDate,
            toFormat: dateFormats.standard.utcDate,
          }).string,
        }),
      );

      const resultThisDate: {
        currentDateName?: string;
        currentDate?: number;
        lastDateName?: string;
        lastDate?: number;
      }[] = [];
      const resultLastDate: {
        currentDateName?: string;
        currentDate?: number;
        lastDateName?: string;
        lastDate?: number;
      }[] = [];
      const chartDetail = { totalAmount: 0, totalAmountGrowth: 0, transactionCount: 0, transactionCountDiff: 0 };

      switch (selectedDateType) {
        // =================================
        // ============== WEEKLY ================
        // =================================

        case SelectedDates.weekly:
          if (thisDate.daily) {
            for (let i = 0; i < thisDate.daily.length; i += 1) {
              resultThisDate.push({
                currentDateName: toJalali({
                  date: thisDate.daily[i].startDate,
                  dateFormat: dateFormats.standard.utcDate,
                  toFormat: dateFormats.ui.jalaliDate,
                }).string,
                currentDate: thisDate.daily[i]?.amount,
              });
            }
          }
          if (lastDate.daily) {
            for (let i = 0; i < lastDate.daily.length; i += 1) {
              resultLastDate.push({
                lastDateName: toJalali({
                  date: lastDate.daily[i].startDate,
                  dateFormat: dateFormats.standard.utcDate,
                  toFormat: dateFormats.ui.jalaliDate,
                }).string,
                lastDate: lastDate.daily[i]?.amount,
              });
            }
          }

          chartDetail.transactionCount = thisDate.transactionCount || 0;
          chartDetail.totalAmount = thisDate.totalAmount || 0;
          chartDetail.transactionCountDiff = (thisDate.transactionCount || 0) - (lastDate.transactionCount || 0);
          chartDetail.totalAmountGrowth = calculateGrowthPercentage(
            lastDate.totalAmount || 0,
            thisDate.totalAmount || 0,
          );

          return { resultThisDate, resultLastDate, chartDetail };

        // =================================
        // ============== MONTHLY ================
        // =================================

        case SelectedDates.monthly:
          if (thisDate.daily) {
            for (let i = 0; i < thisDate.daily.length; i += 1) {
              resultThisDate.push({
                currentDateName: toJalali({
                  date: thisDate.daily[i].startDate,
                  dateFormat: dateFormats.standard.utcDate,
                  toFormat: dateFormats.ui.jalaliDate,
                }).string,
                currentDate: thisDate.daily[i]?.amount,
              });
            }
          }
          if (lastDate.daily) {
            for (let i = 0; i < lastDate.daily.length; i += 1) {
              resultLastDate.push({
                lastDateName: toJalali({
                  date: lastDate.daily[i].startDate,
                  dateFormat: dateFormats.standard.utcDate,
                  toFormat: dateFormats.ui.jalaliDate,
                }).string,
                lastDate: lastDate.daily[i]?.amount,
              });
            }
          }

          /*
            if (thisDate.weekly) {
              for (let i = 0; i < thisDate.weekly.length; i += 1) {
                resultDate.push({
                  currentDateName: toJalali({
                    date: thisDate.weekly[i].startDate,
                    dateFormat: dateFormats.standard.utcDate,
                    toFormat: dateFormats.ui.jalaliDate,
                  }).string,
                  currentDate: thisDate.weekly[i]?.amount,
                });
              }
            }
  
            if (lastDate.weekly) {
              for (let i = 0; i < lastDate.weekly.length; i += 1) {
                resultDate.push({
                  lastDateName: toJalali({
                    date: lastDate.weekly[i].startDate,
                    dateFormat: dateFormats.standard.utcDate,
                    toFormat: dateFormats.ui.jalaliDate,
                  }).string,
                  lastDate: lastDate.weekly[i]?.amount,
                });
              }
            }
            */

          chartDetail.transactionCount = thisDate.transactionCount || 0;
          chartDetail.totalAmount = thisDate.totalAmount || 0;
          chartDetail.transactionCountDiff = (thisDate.transactionCount || 0) - (lastDate.transactionCount || 0);
          chartDetail.totalAmountGrowth = calculateGrowthPercentage(
            lastDate.totalAmount || 0,
            thisDate.totalAmount || 0,
          );

          return { resultThisDate, resultLastDate, chartDetail };

        // =================================
        // ============== YEARLY ================
        // =================================

        case SelectedDates.yearly:
          if (thisDate.monthly) {
            for (let i = 0; i < thisDate.monthly.length; i += 1) {
              resultThisDate.push({
                currentDateName: toJalali({
                  date: thisDate.monthly[i].startDate,
                  dateFormat: dateFormats.standard.utcDate,
                  toFormat: dateFormats.ui.jalaliDate,
                }).string,
                currentDate: thisDate.monthly[i]?.amount,
              });
            }
          }
          if (lastDate.monthly) {
            for (let i = 0; i < lastDate.monthly.length; i += 1) {
              resultLastDate.push({
                lastDateName: toJalali({
                  date: lastDate.monthly[i]?.startDate,
                  dateFormat: dateFormats.standard.utcDate,
                  toFormat: dateFormats.ui.jalaliDate,
                }).string,
                lastDate: lastDate.monthly[i]?.amount,
              });
            }
          }

          chartDetail.transactionCount = thisDate.transactionCount || 0;
          chartDetail.totalAmount = thisDate.totalAmount || 0;
          chartDetail.transactionCountDiff = (thisDate.transactionCount || 0) - (lastDate.transactionCount || 0);
          chartDetail.totalAmountGrowth = calculateGrowthPercentage(
            lastDate.totalAmount || 0,
            thisDate.totalAmount || 0,
          );

          return { resultThisDate, resultLastDate, chartDetail };
        default:
          return {};
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [searchParams],
    {
      success: false,
    },
  );

  const { resultThisDate, chartDetail } = dataChart || {};

  function getDurations(value: SelectedDates) {
    switch (value) {
      case SelectedDates.weekly:
        return { this: dateRanges.thisWeek, pre: dateRanges.prevWeek };
      case SelectedDates.monthly:
        return { this: dateRanges.thisMonth, pre: dateRanges.prevMonth };
      default:
        return { this: dateRanges.thisYear, pre: dateRanges.prevYear };
    }
  }
  const getCurrentSelectedDateForLines = () => {
    switch (selectedDateType) {
      case SelectedDates.weekly:
        return t("overview.report.chart.lines.current.week");
      case SelectedDates.monthly:
        return t("overview.report.chart.lines.current.month");
      case SelectedDates.yearly:
        return t("overview.report.chart.lines.current.year");
      default:
        return undefined;
    }
  };
  const getLastSelectedDateForLines = () => {
    switch (selectedDateType) {
      case SelectedDates.weekly:
        return t("overview.report.chart.lines.last.week");
      case SelectedDates.monthly:
        return t("overview.report.chart.lines.last.month");
      case SelectedDates.yearly:
        return t("overview.report.chart.lines.last.year");
      default:
        return undefined;
    }
  };

  return (
    <LegacyCard.Ant className="tw:w-full">
      {loading ? (
        <Skeleton className="tw:h-85 tw:w-full" />
      ) : (
        <>
          <Row>
            <Col span={24}>
              <Space>
                {/* < این هفته -این ماه - امسال و ایکن > */}
                <Space>
                  {/* < این هفته - این ماه - امسال  > */}
                  <Segmented
                    value={selectedDateType}
                    options={[
                      {
                        label: (
                          <span data-google-track={chartWeekId}>{t("overview.report.chart.header.thisWeek")}</span>
                        ),
                        value: SelectedDates.weekly,
                      },
                      {
                        label: (
                          <span data-google-track={chartMonthId}>{t("overview.report.chart.header.thisMonth")}</span>
                        ),
                        value: SelectedDates.monthly,
                      },
                      {
                        label: (
                          <span data-google-track={chartYearId}>{t("overview.report.chart.header.thisYear")}</span>
                        ),
                        value: SelectedDates.yearly,
                      },
                    ]}
                    onChange={(value) => {
                      const currentDate = getDurations(value as SelectedDates);
                      const newValues = {
                        selectedDateType: value as SelectedDates,
                        currentFromDate: currentDate.this.startDate,
                        currentToDate: currentDate.this.endDate,
                        previousFromDate: currentDate.pre.startDate,
                        previousToDate: currentDate.pre.endDate,
                      };
                      setSearchParams((pre) => ({ ...pre, ...newValues }));
                    }}
                  />
                  <TooltipWeb title={t("overview.report.chart.lines.infoMessage")}>
                    <InfoCircleOutlined width={16} className={"align-center"} />
                  </TooltipWeb>
                </Space>
                {/* </> */}
              </Space>
            </Col>
            <Col span={24} className={"tw:mb-8!"}>
              <Flex vertical>
                <Flex align="center" justify="space-between" wrap="wrap" gap={4}>
                  <Flex gap={8}>
                    <Typography.Text>{t("overview.report.transactions.amount")}</Typography.Text>
                    <TooltipWeb title={t("overview.report.transactions.amountInfoMessage")}>
                      <InfoCircleOutlined width={16} className={"tw:flex tw:items-center"} />
                    </TooltipWeb>
                  </Flex>

                  <Flex gap={2} align="center">
                    <Typography.Title level={3} className={"tw:mb-0!"}>
                      {formatLocalizedNumber({
                        value: chartDetail?.totalAmount || 0,
                        options: { maximumFractionDigits: 2 },
                      })}
                    </Typography.Title>
                    <CuToman width={24} height={24} />
                    {chartDetail?.totalAmount === 0 ? null : (
                      <Typography.Text type={(chartDetail?.totalAmountGrowth || 0) < 1 ? "danger" : "success"}>
                        % {Math.abs(chartDetail?.totalAmountGrowth || 0)}
                        {(chartDetail?.totalAmountGrowth || 0) < 1 ? "-" : "+"}
                      </Typography.Text>
                    )}
                  </Flex>
                </Flex>
                <Flex align="center" justify="space-between" wrap="wrap" gap={4}>
                  <Flex gap={8}>
                    <Typography.Text>{t("overview.report.transactions.count")}</Typography.Text>
                    <TooltipWeb title={t("overview.report.transactions.countInfoMessage")}>
                      <InfoCircleOutlined width={16} className={"tw:flex tw:items-center"} />
                    </TooltipWeb>
                  </Flex>

                  <Flex gap={2} align="center">
                    <Typography.Title level={3} className={"tw:mb-0!"}>
                      {chartDetail?.transactionCount || 0}
                    </Typography.Title>
                    <Typography.Title level={5} className="tw:m-0!">
                      {t("common.transaction")}
                    </Typography.Title>
                    {chartDetail?.transactionCount === 0 ? null : (
                      <Typography.Text type={(chartDetail?.transactionCountDiff || 0) < 1 ? "danger" : "success"}>
                        {Math.abs(chartDetail?.transactionCountDiff || 0)}
                        {(chartDetail?.transactionCountDiff || 0) < 1 ? "-" : "+"}
                      </Typography.Text>
                    )}
                  </Flex>
                </Flex>
              </Flex>
            </Col>
          </Row>

          <Row>
            <Col span={24} className={"tw:max-h-full tw:w-full"}>
              <div className="tw:relative tw:h-full tw:w-full">
                {resultThisDate?.length ? null : (
                  <div className="tw:absolute tw:bottom-0 tw:left-0 tw:right-0 tw:top-0 tw:flex tw:flex-col tw:items-center tw:justify-center">
                    <EmptyChart className={"color--cta mb-1"} strokeWidth={"0.5"} fill={"#0845BF11"} width={"66px"} />
                    {t("overview.report.chart.emptyChart")}
                  </div>
                )}
                <ResponsiveContainer height={260} width={"99%"} minHeight={260} minWidth={"99%"}>
                  <LineChart
                    data={resultThisDate}
                    margin={{
                      top: 5,
                      right: 30,
                      left: 20,
                      bottom: 5,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis
                      dataKey="currentDateName"
                      interval={md ? "preserveStartEnd" : 0}
                      tickFormatter={(value, index) => {
                        if (!md) {
                          // Display only first and last ticks on small screens
                          if (index === 0 || index === (resultThisDate?.length || 0) - 1) {
                            return value;
                          }
                          return ""; // Hide other ticks
                        }
                        return value; // Show all ticks on larger screens
                      }}
                    />
                    {resultThisDate?.length && <Tooltip />}
                    <Legend />
                    <Line
                      name={getCurrentSelectedDateForLines()}
                      type="monotone"
                      dataKey="currentDate"
                      stroke="#8884d8"
                      activeDot={{ r: 8 }}
                    />
                    <Line name={getLastSelectedDateForLines()} type="monotone" dataKey="lastDate" stroke="#82ca9d" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Col>
          </Row>
        </>
      )}
    </LegacyCard.Ant>
  );
};

export default ReportMobile;
